import React, { useEffect, useState } from 'react';
import { User, Role } from '../types';
import * as db from '../services/db';
import { NAV_ITEMS, NAV_GROUPS } from '../navConfig';
import './Products.css';
import './Users.css';

// User / Operator master (Android UserProfile / Employee). Manages app users
// with a quick PIN and role, and selects the current operator whose name is
// stamped onto bills (Transaction.userId / userName) and the audit log.
// The Roles & Rights tab edits each role's rights matrix — the drawer pages
// that role may open (enforced in Navigation and by the App route gate).
const blankUser = { name: '', pin: '', role: 'cashier', mobile: '', active: true };

const Users: React.FC = () => {
  const [tab, setTab] = useState<'users' | 'roles'>('users');

  // ---- users state ----
  const [users, setUsers] = useState<User[]>([]);
  const [currentId, setCurrentId] = useState<string | null>(null);
  const [editing, setEditing] = useState<User | null>(null);
  const [f, setF] = useState({ ...blankUser });

  // ---- roles state ----
  const [roles, setRoles] = useState<Role[]>([]);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [roleName, setRoleName] = useState('');
  const [rights, setRights] = useState<string[]>([]);

  // Storage is async; load users, the current operator and roles on mount.
  useEffect(() => {
    let alive = true;
    Promise.all([db.getUsers(), db.getCurrentUserId(), db.getRoles()]).then(([u, cid, r]) => {
      if (!alive) return;
      setUsers(u);
      setCurrentId(cid);
      setRoles(r);
    });
    return () => {
      alive = false;
    };
  }, []);

  const set = (k: keyof typeof blankUser, v: string | boolean) => setF((prev) => ({ ...prev, [k]: v }));

  const persist = (next: User[]) => {
    db.saveUsers(next);
    setUsers(next);
  };

  const reset = () => {
    setF({ ...blankUser });
    setEditing(null);
  };

  const save = () => {
    if (!f.name.trim()) return alert('Name is required.');
    if (!/^\d{4,6}$/.test(f.pin)) return alert('PIN must be 4–6 digits.');
    if (editing) {
      persist(users.map((u) => (u.id === editing.id ? { ...editing, ...f, name: f.name.trim() } : u)));
    } else {
      persist([...users, { id: db.newId(), ...f, name: f.name.trim() }]);
    }
    reset();
  };

  const edit = (u: User) => {
    setEditing(u);
    setF({ name: u.name, pin: u.pin, role: u.role, mobile: u.mobile ?? '', active: u.active });
  };

  const remove = () => {
    if (editing && window.confirm('Delete this user?')) {
      persist(users.filter((u) => u.id !== editing.id));
      if (currentId === editing.id) login(null);
      reset();
    }
  };

  const login = (id: string | null) => {
    db.setCurrentUserId(id);
    setCurrentId(id);
    const u = id ? users.find((x) => x.id === id) : null;
    db.logEvent({ type: 'login', detail: u ? `Logged in as ${u.name}` : 'Logged out', userName: u?.name });
  };

  // ---- roles & rights ----

  const roleLabel = (id: string) => roles.find((r) => r.id === id)?.name ?? id;

  const persistRoles = async (next: Role[]) => {
    await db.saveRoles(next);
    setRoles(await db.getRoles());
  };

  const resetRole = () => {
    setEditingRole(null);
    setRoleName('');
    setRights([]);
  };

  const editRole = (r: Role) => {
    setEditingRole(r);
    setRoleName(r.name);
    setRights([...r.rights]);
  };

  const saveRole = async () => {
    const name = roleName.trim();
    if (!name) return alert('Role name is required.');
    if (roles.some((r) => r.name.toLowerCase() === name.toLowerCase() && r.id !== editingRole?.id)) {
      return alert('A role with that name already exists.');
    }
    const userName = (await db.getCurrentUser())?.name;
    if (editingRole) {
      persistRoles(roles.map((r) => (r.id === editingRole.id ? { ...r, name, rights } : r)));
      db.logEvent({ type: 'settings', detail: `Role '${name}' updated (${rights.length} rights)`, userName });
    } else {
      persistRoles([...roles, { id: db.newId(), name, rights }]);
      db.logEvent({ type: 'settings', detail: `Role '${name}' created`, userName });
    }
    resetRole();
  };

  const removeRole = async () => {
    if (!editingRole) return;
    if (editingRole.system) return alert('The admin role cannot be deleted.');
    const inUse = users.filter((u) => u.role === editingRole.id);
    if (inUse.length > 0) {
      return alert(`Cannot delete: ${inUse.length} user(s) have this role (${inUse.map((u) => u.name).join(', ')}).`);
    }
    if (window.confirm('Delete this role?')) {
      persistRoles(roles.filter((r) => r.id !== editingRole.id));
      db.logEvent({ type: 'settings', detail: `Role '${editingRole.name}' deleted`, userName: (await db.getCurrentUser())?.name });
      resetRole();
    }
  };

  const toggleRight = (path: string) =>
    setRights((prev) => (prev.includes(path) ? prev.filter((p) => p !== path) : [...prev, path]));

  const toggleGroup = (group: string) => {
    const paths = NAV_ITEMS.filter((i) => i.group === group).map((i) => i.path);
    const allOn = paths.every((p) => rights.includes(p));
    setRights((prev) => (allOn ? prev.filter((p) => !paths.includes(p)) : Array.from(new Set([...prev, ...paths]))));
  };

  const locked = !!editingRole?.system;

  const lbl = (t: string) => <label className="pf-lbl">{t}</label>;

  const tabs = (
    <div className="pf-typeseg" style={{ marginRight: 'auto' }}>
      <button className={tab === 'users' ? 'on' : ''} onClick={() => setTab('users')}>Users</button>
      <button className={tab === 'roles' ? 'on' : ''} onClick={() => setTab('roles')}>Roles &amp; Rights</button>
    </div>
  );

  if (tab === 'roles') {
    return (
      <div className="pm-page users-page">
        {/* role list */}
        <div className="pm-list card-rounded">
          <div className="pm-list-scroll">
            {roles.map((r) => (
              <button key={r.id} className={`pm-list-item ${editingRole?.id === r.id ? 'sel' : ''}`} onClick={() => editRole(r)}>
                <span className="pm-li-name">{r.name}</span>
                <span className="pm-li-meta">
                  {r.system ? 'all pages · built-in' : `${r.rights.length} of ${NAV_ITEMS.length} pages`}
                  {' · '}{users.filter((u) => u.role === r.id).length} user(s)
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* role form + rights matrix */}
        <div className="pm-form card-rounded">
          <div className="pf-top">
            {tabs}
            <div className="pf-top-btns">
              {editingRole && <button className="pf-btn-orange" onClick={resetRole}>New</button>}
              {editingRole && !locked && <button className="pf-btn-orange" onClick={removeRole}>Delete</button>}
              {!locked && <button className="pf-btn-orange" onClick={saveRole}>{editingRole ? 'Update' : 'Save'}</button>}
            </div>
          </div>

          <div className="pf-scroll">
            <div className="pf-row r2">
              <div>
                {lbl('Role name *')}
                <input value={roleName} onChange={(e) => setRoleName(e.target.value)} disabled={locked} placeholder="e.g. supervisor" />
              </div>
            </div>

            {locked && (
              <p className="muted" style={{ marginBottom: 14 }}>
                The admin role is built in: it always has every right and cannot be edited or deleted.
              </p>
            )}

            <div className="pf-section">Rights — pages this role can open</div>
            {NAV_GROUPS.map((group) => {
              const items = NAV_ITEMS.filter((i) => i.group === group);
              const allOn = items.every((i) => rights.includes(i.path));
              return (
                <div key={group} style={{ marginBottom: 14 }}>
                  <label className="pf-lbl" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input type="checkbox" checked={allOn} onChange={() => toggleGroup(group)} disabled={locked} />
                    {group}
                  </label>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '4px 12px', paddingLeft: 24 }}>
                    {items.map((i) => (
                      <label key={i.path} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}>
                        <input type="checkbox" checked={rights.includes(i.path)} onChange={() => toggleRight(i.path)} disabled={locked} />
                        {i.icon} {i.label}
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pm-page users-page">
      {/* list */}
      <div className="pm-list card-rounded">
        <div className="pm-list-scroll">
          {users.map((u) => (
            <button key={u.id} className={`pm-list-item ${editing?.id === u.id ? 'sel' : ''}`} onClick={() => edit(u)}>
              <span className="pm-li-name">
                {u.name} {currentId === u.id ? '✓' : ''}
              </span>
              <span className="pm-li-meta">
                {roleLabel(u.role)}
                {u.active ? '' : ' · inactive'}
              </span>
            </button>
          ))}
          {users.length === 0 && (
            <p className="muted" style={{ padding: 16 }}>
              No users yet. Add one to stamp bills with an operator.
            </p>
          )}
        </div>
      </div>

      {/* form */}
      <div className="pm-form card-rounded">
        <div className="pf-top">
          {tabs}
          <span style={{ fontWeight: 700, color: 'var(--color-primary-dark)' }}>
            {editing ? 'Edit User' : 'New User'}
          </span>
          <div className="pf-top-btns">
            {editing && <button className="pf-btn-orange" onClick={reset}>New</button>}
            {editing && <button className="pf-btn-orange" onClick={remove}>Delete</button>}
            <button className="pf-btn-orange" onClick={save}>{editing ? 'Update' : 'Save'}</button>
          </div>
        </div>

        <div className="pf-scroll">
          <div className="pf-row r2">
            <div>{lbl('Name *')}<input value={f.name} onChange={(e) => set('name', e.target.value)} /></div>
            <div>{lbl('Mobile')}<input value={f.mobile} onChange={(e) => set('mobile', e.target.value)} /></div>
          </div>
          <div className="pf-row r3">
            <div>{lbl('Quick PIN * (4–6 digits)')}<input value={f.pin} onChange={(e) => set('pin', e.target.value)} /></div>
            <div>
              {lbl('Role')}
              <select value={f.role} onChange={(e) => set('role', e.target.value)}>
                {roles.map((r) => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </select>
            </div>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <label className="pf-check"><input type="checkbox" checked={f.active} onChange={(e) => set('active', e.target.checked)} /> Active</label>
            </div>
          </div>

          {editing && (
            <>
              <div className="pf-section">Operator</div>
              {currentId === editing.id ? (
                <button className="pf-btn-grey" onClick={() => login(null)}>Log out {editing.name}</button>
              ) : (
                <button className="pf-btn-teal" onClick={() => login(editing.id)} disabled={!editing.active}>
                  Set as current operator
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Users;
